import { FC, useState } from "react";

import {
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import {
  CameraPhotoWithCommentType,
  DriverCamera,
  DriverFlowBottomButton,
} from "../blocks";
import { DsText } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUploadServicePhotoMutation } from "../data/mutations/storageMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { JobServiceTaskBaseType } from "../domain/task";
import { DriverTopActionButton } from "./DriverTopActionButton";

const DriverAddPhotoDroppedBin: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();

  const { mutate: uploadServicePhoto, isPending: isUploadingServicePhoto } =
    useUploadServicePhotoMutation();

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const openCamera = () => setIsCameraOpen(true);

  const closeCamera = () => setIsCameraOpen(false);

  const isDumpAndReturnTask = taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN;

  const uploadPhoto = ({
    base64PhotoString,
    comment,
  }: CameraPhotoWithCommentType) => {
    uploadServicePhoto(
      {
        jobId,
        serviceId,
        base64PhotoString,
        baseName: "siteDropPhoto",
      },
      {
        onSuccess: path => {
          const driverFlowStatus =
            taskId === ServiceTaskTypeEnum.EXCHANGEBIN
              ? TaskDriverStatusEnum.DRIVER_PICK_UP_BIN_ON_SITE
              : TaskDriverStatusEnum.TASK_REVIEW;

          updateService({
            jobId,
            serviceId,
            siteDropPhoto: {
              photoUrl: path,
              comment,
            },
            turnaroundPhoto: null,
            driverFlowStatus,
          });

          updateTask({
            jobId,
            serviceId,
            taskId,
            driverFlowStatus,
          });
        },
      },
    );
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isUploadingServicePhoto ||
    (isLoading &&
      variables?.driverFlowStatus ===
        TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN);

  const previousDriverFlowStatus = isDumpAndReturnTask
    ? TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE
    : TaskDriverStatusEnum.SELECTING_BIN_FOR_SERVICE;

  const isPreviousButtonLoading =
    isLoading && variables?.driverFlowStatus === previousDriverFlowStatus;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: previousDriverFlowStatus,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: previousDriverFlowStatus,
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {isCameraOpen ? (
        <DriverCamera
          mode="single-photo"
          onChoosePhoto={photo => {
            setIsCameraOpen(false);
            void uploadPhoto(photo);
          }}
          onCancelClick={closeCamera}
        />
      ) : (
        <>
          <DriverTopActionButton
            jobId={jobId}
            serviceId={serviceId}
            taskId={taskId}
          />
          <div className="flex flex-grow w-full flex-col justify-center">
            <DsText variant="pageTitle">
              {isDumpAndReturnTask
                ? t("driver_photo_dropped_bin.returned_bin_title")
                : t("driver_photo_dropped_bin.title")}
            </DsText>
          </div>
          <DriverFlowBottomButton
            onNextButtonClick={openCamera}
            isNextButtonLoading={isUploadingServicePhoto || isNextButtonLoading}
            nextButtonTitle="take_photo"
            previousButtonTitle={
              isDumpAndReturnTask
                ? "driver_photo_dropped_bin.returned_bin_back_button"
                : "driver_photo_dropped_bin.back_button"
            }
            onPreviousButtonClick={goToPreviousStep}
            isPreviousButtonLoading={isPreviousButtonLoading}
          />
        </>
      )}
    </div>
  );
};

export { DriverAddPhotoDroppedBin };
